
  import heic2any from "heic2any";
  import {imageUploader} from "~/utils/imageUploader";

  export default {
    name: 'ImageCropper',
    data() {
      return {
        imageUrl: null,
        croppedImageUrl: this.$store.state.cartItem.messageCardImage || null,
        scale: 1,
        initialScale: 1,
        actualCropSize: 1000, // Actual size of the cropped image (1000x1000)
        displayCropSize: 300, // Display size of the crop area (300x300)
        cropPosition: {x: 0, y: 0},
        imagePosition: {x: 0, y: 0},
        initialImagePosition: {x: 0, y: 0},
        isImageDragging: false,
        imageDragStart: {x: 0, y: 0},
        imageElement: null,
        imageNaturalWidth: 0,
        imageNaturalHeight: 0,
        isMobile: false,
        errorMessage: null,
        // Pinch zoom variables
        touchStartDistance: 0,
        touchStartScale: 1,
        isPinching: false,
        // Touch center point for zooming
        touchCenter: { x: 0, y: 0 },
        // Scroll offset for better visibility
        scrollOffset: 300, // Updated to 300px for desktop
        // Store original image for edit functionality
        originalImageUrl: null,
        // Drag and drop state
        isDragging: false,
        disableContinue: false,
        imageUploaderValidation: true,
      }
    },
    mounted() {
      // Check if we're on a mobile device
      this.isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      // Adjust crop size for mobile - now 250x250 for all mobile devices
      if (this.isMobile) {
        this.displayCropSize = 250;
      }

      // Adjust scroll offset for mobile - updated to 200px
      if (this.isMobile) {
        this.scrollOffset = 200;
      }

      // Add global event listeners for drag and drop
      this.addDragDropListeners();
    },
    async created() {
      this.$nuxt.$on("runInputFieldsValidation", async () => {
      this.handleScrollError();
    });
    },
    beforeDestroy() {
      // Clean up global event listeners
      this.removeDragDropListeners();
    },
    methods: {
      // Drag and drop methods
      addDragDropListeners() {
        // Prevent default drag behaviors on the entire document
        document.addEventListener('dragover', this.preventDefaultDrag);
        document.addEventListener('drop', this.preventDefaultDrag);
      },

      removeDragDropListeners() {
        document.removeEventListener('dragover', this.preventDefaultDrag);
        document.removeEventListener('drop', this.preventDefaultDrag);
      },

      preventDefaultDrag(e) {
        e.preventDefault();
        e.stopPropagation();
      },

      onDragOver(e) {
        this.isDragging = true;
        e.preventDefault();
      },

      onDragLeave(e) {
        this.isDragging = false;
        e.preventDefault();
      },

      onDrop(e) {
        e.preventDefault();
        this.isDragging = false;

        // Get the dropped files
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          // Process the first file
          this.processFile(files[0]);
        }
      },

      processFile(file) {
        if (!file) return;

        this.errorMessage = null;
        const maxSize = 3 * 1024 * 1024;

        // If HEIC, convert to JPEG
        if (file.type === "image/heic" || file.name.endsWith(".heic")) {
          if (file.size > maxSize) {
            this.errorMessage = `Oops! That image is a bit too big. Could you try uploading one under 3MB? Or text us at <a href="tel:1-888-618-1211">1-888-618-1211</a> — we’d love to help!`;
            return;
          }

          heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.9
          })
            .then((convertedBlob) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                this.imageUrl = e.target.result;
                this.originalImageUrl = e.target.result;
                this.croppedImageUrl = null;
                this.$store.dispatch("cartItem/SET_MSG_CARD_IMG", null);

                const tempImg = new Image();
                tempImg.onload = () => {
                  this.imageNaturalWidth = tempImg.width;
                  this.imageNaturalHeight = tempImg.height;

                  if (tempImg.width < 500 || tempImg.height < 500) {
                    this.errorMessage = 'Image is too small. Please upload at least 500x500 pixels.';
                  }

                  this.$nextTick(() => {
                    if (this.$refs.image && !this.errorMessage) {
                      this.fitImageToContainer();
                    }
                  });
                };
                tempImg.src = e.target.result;
              };
              reader.readAsDataURL(convertedBlob);
            })
            .catch((err) => {
              this.errorMessage = 'Failed to process HEIC image.';
              console.error(err);
            });

          return; // Don't continue with normal path
        }

        // JPEG/PNG logic
        if (!file.type.match('image/(jpeg|jpg|png)')) {
          this.errorMessage = 'Only PNG and JPG/JPEG files are allowed';
          return;
        }

        if (file.size > maxSize) {
          this.errorMessage = `Oops! That image is a bit too big. Could you try uploading one under 3MB? Or text us at <a href="tel:1-888-618-1211" class="underline text-inherit">1-888-618-1211</a> — we’d love to help!`;
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result;
          this.originalImageUrl = e.target.result;
          this.croppedImageUrl = null;
          this.$store.dispatch("cartItem/SET_MSG_CARD_IMG", null);

          // Create a temporary image to get the natural dimensions
          const tempImg = new Image();
          tempImg.crossOrigin = "anonymous";
          tempImg.onload = () => {
            this.imageNaturalWidth = tempImg.width;
            this.imageNaturalHeight = tempImg.height;

            // Check if image is too small
            if (this.imageNaturalWidth < 500 || this.imageNaturalHeight < 500) {
              this.errorMessage = 'Image is too small. Please upload an image that is at least 500x500 pixels.';
            }

            this.$nextTick(() => {
              if (this.$refs.image && !this.errorMessage) {
                this.fitImageToContainer();
              }
            });
          };
          tempImg.src = e.target.result;
        };
        reader.readAsDataURL(file);
      },

      onFileChange(e) {
        const file = e.target.files[0];
        if (file) {
          this.processFile(file);
        }
        this.$nuxt.$emit("messageImageUploaded", false);
      },

      clearImage() {
        // Clear the current image and reset the component
        this.imageUrl = null;
        this.originalImageUrl = null;
        this.croppedImageUrl = null;
        this.$store.dispatch("cartItem/SET_MSG_CARD_IMG", null);
        this.scale = 1
        this.imagePosition = {x: 0, y: 0}
        this.errorMessage = null

        // Reset the file input to allow selecting the same file again
        const fileInput = document.getElementById('image-upload')
        if (fileInput) {
          fileInput.value = ''
        }

        // Scroll to upload section after a short delay to ensure DOM is updated
        this.$nextTick(() => {
          this.$nuxt.$emit("imageUploaderView", true);
        });
      },

      // New method for "Add New" button
      addNewImage() {
        this.$nuxt.$emit("cardMessageDisabled", true);
        this.clearImage();
        this.handleScrollError();
      },

      // New method for "Edit" button
      editImage() {
        if (this.originalImageUrl) {
          this.$nuxt.$emit("cardMessageDisabled", true);
          this.imageUrl = this.originalImageUrl;
          this.croppedImageUrl = null;
          this.$store.dispatch("cartItem/SET_MSG_CARD_IMG", null);

          // Reset to initial state for editing
          this.$nextTick(() => {
            if (this.$refs.image) {
              this.fitImageToContainer();
            }
          });
        }
      },

      fitImageToContainer() {
        const cropSize = this.displayCropSize;

        // Calculate the scale required to fit the image exactly inside the crop area
        const scaleX = cropSize / this.imageNaturalWidth;
        const scaleY = cropSize / this.imageNaturalHeight;

        // Use the larger scale to ensure the image covers the entire crop area
        const fitScale = Math.max(scaleX, scaleY);

        // Set initial scale (and clamp to 1 to avoid upscaling small images)
        this.scale = Math.min(fitScale, 1);
        this.initialScale = this.scale;

        // Calculate image size after scaling
        const scaledWidth = this.imageNaturalWidth * this.scale;
        const scaledHeight = this.imageNaturalHeight * this.scale;

        // Center the image inside the container
        const container = this.$refs.canvasContainer;
        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;

        this.imagePosition = {
          x: Math.round((containerWidth - scaledWidth) / 2),
          y: Math.round((containerHeight - scaledHeight) / 2),
        };
        this.initialImagePosition = { ...this.imagePosition };

        // Update image transform and crop area
        this.updateImageTransform();
        this.centerCropArea();
      }
      ,

      centerCropArea() {
        const container = this.$refs.canvasContainer
        // Always position the crop box in the center of the container
        this.cropPosition = {
          x: (container.offsetWidth - this.displayCropSize) / 2,
          y: (container.offsetHeight - this.displayCropSize) / 2
        }
      },
      zoomIn() {
        const currentScale = parseFloat(this.scale);
        const step = 0.02; // Changed from 0.05 to 0.02 for more gradual zooming

        if (currentScale < 2) {
          const newScale = Math.min(2, currentScale + step);
          this.scale = parseFloat(newScale.toFixed(2));
          this.onZoomChange();
        }
      },
      zoomOut() {
        const currentScale = parseFloat(this.scale);
        const step = 0.02; // Changed from 0.05 to 0.02

        if (currentScale > this.initialScale) {
          const newScale = Math.max(this.initialScale, currentScale - step);
          this.scale = parseFloat(newScale.toFixed(2));
          this.onZoomChange();
        } else if (currentScale.toFixed(2) === this.initialScale.toFixed(2)) {
          this.onZoomChange();
        }
      },
      onZoomChange() {
        const container = this.$refs.canvasContainer;
        if (!container) return;

        const scaleRounded = parseFloat(this.scale.toFixed(2));
        const initialRounded = parseFloat(this.initialScale.toFixed(2));

        if (scaleRounded === initialRounded) {
          // ✅ Snap to initial position when scale returns to original
          this.imagePosition = {
            x: Math.round(this.initialImagePosition.x),
            y: Math.round(this.initialImagePosition.y)
          };
        } else {
          // 💡 Otherwise, keep image centered relative to zoom
          const containerWidth = container.offsetWidth;
          const containerHeight = container.offsetHeight;

          const centerX = containerWidth / 2;
          const centerY = containerHeight / 2;

          const currentCenterX = this.imagePosition.x + (this.imageNaturalWidth * this.scale) / 2;
          const currentCenterY = this.imagePosition.y + (this.imageNaturalHeight * this.scale) / 2;

          const offsetX = centerX - currentCenterX;
          const offsetY = centerY - currentCenterY;

          this.imagePosition = {
            x: this.imagePosition.x + offsetX,
            y: this.imagePosition.y + offsetY
          };
        }

        this.updateImageTransform();
      }
      ,

      updateImageTransform() {
        if (this.$refs.image) {
          this.$refs.image.style.transform = `translate(${this.imagePosition.x}px, ${this.imagePosition.y}px) scale(${this.scale})`
        }
      },

      // Handle touch events for image dragging and pinch zoom
      handleTouchStart(e) {
        if (!this.imageUrl || !this.isMobile) return

        // If we have two fingers on the screen, initialize pinch-to-zoom
        if (e.touches.length === 2) {
          // Calculate the initial distance between the two touch points
          const touch1 = e.touches[0];
          const touch2 = e.touches[1];

          // Calculate the center point between the two touches
          this.touchCenter = {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
          };

          this.touchStartDistance = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
          );
          this.touchStartScale = this.scale;
          this.isPinching = true;

          // Prevent default to avoid other touch handlers
          e.preventDefault();
        } else if (e.touches.length === 1) {
          // Single touch - start image dragging
          this.isImageDragging = true;

          const touch = e.touches[0];
          this.imageDragStart = {
            x: touch.clientX - this.imagePosition.x,
            y: touch.clientY - this.imagePosition.y
          };

          e.preventDefault();
        }
      },

      handleTouchMove(e) {
        if (!this.imageUrl || !this.isMobile) return

        // If we're pinching with two fingers
        if (e.touches.length === 2 && this.isPinching) {
          const touch1 = e.touches[0];
          const touch2 = e.touches[1];

          // Calculate the current distance between touch points
          const currentDistance = Math.hypot(
            touch2.clientX - touch1.clientX,
            touch2.clientY - touch1.clientY
          );

          // Update the center point between the two touches
          this.touchCenter = {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
          };

          // Calculate the new scale based on the change in distance
          const newScale = this.touchStartScale * (currentDistance / this.touchStartDistance);

// Store the old scale for position adjustment
          const oldScale = this.scale;

// Limit the scale to min/max values (prevent zooming out below initial scale)
          this.scale = Math.max(this.initialScale, Math.min(2, newScale)); // Changed from 3 to 2

          // Calculate the scale change ratio
          const scaleRatio = this.scale / oldScale;

          // Get container bounds
          const container = this.$refs.canvasContainer;
          const containerRect = container.getBoundingClientRect();

          // Calculate the touch point relative to the container
          const touchX = this.touchCenter.x - containerRect.left;
          const touchY = this.touchCenter.y - containerRect.top;

          // Calculate the distance from touch point to image position
          const distanceX = touchX - this.imagePosition.x;
          const distanceY = touchY - this.imagePosition.y;

          // Adjust the image position to zoom toward/from the touch point
          this.imagePosition = {
            x: touchX - distanceX * scaleRatio,
            y: touchY - distanceY * scaleRatio
          };

          this.updateImageTransform();

          // Prevent default to avoid scrolling
          e.preventDefault();
        } else if (e.touches.length === 1 && this.isImageDragging) {
          // Single touch - move the image
          const touch = e.touches[0];

          this.imagePosition = {
            x: touch.clientX - this.imageDragStart.x,
            y: touch.clientY - this.imageDragStart.y
          };

          this.updateImageTransform();
          e.preventDefault();
        }
      },

      handleTouchEnd(e) {
        // Reset states
        if (e.touches.length < 2) {
          this.isPinching = false;
        }

        if (e.touches.length === 0) {
          this.isImageDragging = false;
        }
      },

      // Mouse events for image dragging
      startImageDrag(e) {
        if (!this.imageUrl) return;

        this.isImageDragging = true;
        this.imageDragStart = {
          x: e.clientX - this.imagePosition.x,
          y: e.clientY - this.imagePosition.y
        };

        e.preventDefault();
      },

      onImageDrag(e) {
        if (!this.isImageDragging) return;

        this.imagePosition = {
          x: e.clientX - this.imageDragStart.x,
          y: e.clientY - this.imageDragStart.y
        };

        this.updateImageTransform();
        e.preventDefault();
      },

      stopImageDrag() {
        this.isImageDragging = false;
      },

      resetImage() {
        // Reset to the initial scale and position
        this.scale = this.initialScale
        this.imagePosition = {...this.initialImagePosition}

        this.updateImageTransform()
        this.centerCropArea()
        this.croppedImageUrl = null;
        this.$store.dispatch("cartItem/SET_MSG_CARD_IMG", null);
      },

      async cropImage() {
        if (!this.imageUrl || !this.$refs.image) return;

        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = this.$refs.image;

          canvas.width = this.actualCropSize;
          canvas.height = this.actualCropSize;

          const cropX = (this.cropPosition.x - this.imagePosition.x) / this.scale;
          const cropY = (this.cropPosition.y - this.imagePosition.y) / this.scale;
          const cropWidth = this.displayCropSize / this.scale;
          const cropHeight = this.displayCropSize / this.scale;

          ctx.drawImage(
            img,
            cropX, cropY, cropWidth, cropHeight,
            0, 0, this.actualCropSize, this.actualCropSize
          );

          this.croppedImageUrl = canvas.toDataURL('image/png');

          await this.$store.dispatch("cartItem/SET_MSG_CARD_IMG", this.croppedImageUrl);
          const imageUrl = await imageUploader(this.croppedImageUrl);
          if (imageUrl) {
            this.$nuxt.$emit("messageImageUploaded", true);
            await this.$store.dispatch("cartItem/SET_CUSTOM_CARD_IMG", imageUrl);
          }

        } catch (error) {
          console.error('Error cropping/uploading image:', error);
        }
      },
      handleScrollError() {
        this.imageUploaderValidation = this.croppedImageUrl;
      }
    }
  }
